import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface TabOverflowContextProps {
  updateTabWidth: (id: string, width: number) => void;
  containerWidth: number;
}

const TabOverflowContext = createContext<TabOverflowContextProps | undefined>(undefined);

export const useTabOverflowContext = (): TabOverflowContextProps => {
  const context = useContext(TabOverflowContext);
  if (!context) {
    throw new Error('useTabOverflowContext must be used within a TabOverflowProvider');
  }
  return context;
};

interface TabOverflowProviderProps {
  children: ReactNode;
  gapSize?: number;
  buffer?: number;
}

export const TabOverflowProvider = ({ children, gapSize = 16, buffer = 0 }: TabOverflowProviderProps) => {
  const [tabWidths, setTabWidths] = useState<Record<string, number>>({});

  const updateTabWidth = useCallback((value: string, width: number) => {
    setTabWidths((prevWidths) => {
      const newWidths = { ...prevWidths };
      newWidths[value] = width;
      return newWidths;
    });
  }, []);

  const tabsWithWidths = Object.values(tabWidths).filter((width) => width).length;
  const totalWidth = Object.values(tabWidths).reduce((acc, width) => acc + width, 0);
  const totalWidthWithGap = totalWidth + gapSize * (tabsWithWidths - 1) + buffer;

  const value = useMemo(
    () => ({ updateTabWidth, containerWidth: totalWidthWithGap }),
    [updateTabWidth, totalWidthWithGap],
  );

  return <TabOverflowContext.Provider value={value}>{children}</TabOverflowContext.Provider>;
};
